import { action, computed, makeObservable, observable } from 'mobx';

import { IApiClient } from '../../../../app/data/client';
import { SearchDealersToSupportQuery, SetContactLastDealerUsedCommand } from '../../../../app/data/model';
import type { OrganizationData, ShoppingContext, StoreData } from '../../../../app/data/model';
import { WarningStateBase } from '../../../../app/layout/Warnings/WarningStateBase';
import { AsyncCommand, debounce } from '../../../../app/shared/common';
import { IEventAggregator } from '../../../../app/shared/EventAggregator';
import Logger from '../../../../app/shared/Logger';
import { CustomerUpdatedEvent } from '../../../cart/events/CustomerUpdatedEvent';
import { StoreSelectorTranslation } from '../../../localization/SiteTranslation';

class StoreSelectorState {
  @observable
  public isOpen: boolean = false;

  @observable
  public searchPhrase: string = '';

  @observable.ref
  public searchResults: OrganizationData[] = [];

  @observable
  public isSearching: boolean = false;

  @observable
  public isNoResultsMessageVisible: boolean = false;

  @computed
  public get hasResults(): boolean {
    return this.searchPhrase.length > 0 && this.searchResults.length > 0;
  }

  public readonly changeStoreCommand = new AsyncCommand<string>((storeId) => this.changeStore(storeId));

  public readonly storeChangeFailedWarning = new WarningStateBase();

  public constructor(
    private readonly client: IApiClient,
    private readonly eventAggregator: IEventAggregator,
    private readonly shoppingContext: ShoppingContext,
    public readonly currentStore: StoreData,
    public readonly stores: StoreData[],
    public readonly translation: StoreSelectorTranslation,
  ) {
    makeObservable(this);

    this.currentStore = currentStore;
    this.stores = stores.filter((s) => s.id !== currentStore.id);
  }

  @action.bound
  public close() {
    this.isOpen = false;
  }

  @action.bound
  public toggle() {
    this.isOpen = !this.isOpen;
  }

  @action.bound
  public setSearchPhrase(phrase: string) {
    this.searchPhrase = phrase;
    if (!phrase) {
      this.searchResults = [];
      this.isNoResultsMessageVisible = false;
    } else {
      this.triggerSearch();
    }
  }

  @action.bound
  public async selectStore(storeId: string) {
    this.close();
    await this.changeStoreCommand.invoke(storeId);
  }

  @action.bound
  private setIsSearching(value: boolean) {
    this.isSearching = value;
  }

  @action.bound
  private setSearchResults(results: OrganizationData[]) {
    this.searchResults = results;
    this.isNoResultsMessageVisible = results.length === 0 && this.searchPhrase.length > 0;
  }

  // tslint:disable-next-line: member-ordering
  private readonly triggerSearch = debounce(() => void this.searchOrganizations(), 300);

  private async searchOrganizations() {
    if (!this.searchPhrase) {
      return;
    }
    this.setIsSearching(true);
    try {
      const result = await this.client.send(new SearchDealersToSupportQuery({ searchPhrase: this.searchPhrase }));
      this.setSearchResults(result.dealers);
    } catch (e) {
      Logger.error(`Search organizations failed: ${JSON.stringify(e)}`);
    } finally {
      this.setIsSearching(false);
    }
  }

  private async changeStore(storeId: string) {
    try {
      await this.client.send(
        new SetContactLastDealerUsedCommand({
          storeId: storeId,
          shoppingContext: this.shoppingContext,
        }),
      );
    } catch (e) {
      Logger.error(
        `Unable to set ${storeId} as a default organization for the ${
          this.shoppingContext.customer.userName
        } user. Error: ${JSON.stringify(e)}`,
      );
      this.storeChangeFailedWarning.open();
      return;
    }

    this.eventAggregator.publish(
      new CustomerUpdatedEvent({
        username: this.shoppingContext.customer.userName,
        languageCode: this.shoppingContext.languageCode,
        storeId: storeId,
      }),
    );
  }
}

export default StoreSelectorState;
